/**
 * @file Calendar add-entry decision modal owner.
 */

import { closeForm } from "../../shared/forms/modalShell.js";
import { openStandardForm } from "../../shared/forms/standardFormModal.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";
import {
    isDrugsAndLeaveActionLocked,
} from "../../shared/utils/actionLock.js";
import { getCalendarDateKey } from "./calendarDateUtils.js";
import { openEventFormModal } from "./eventFormModal.js";
import { openLeaveRequestModal } from "./leaveRequestModal.js";
import { openReminderFormModal } from "./reminderFormModal.js";

function attrValue(value) {
    return escapeHTML(String(value ?? ""))
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function getTodayDateKey() {
    return getCalendarDateKey(new Date());
}

function getInitialDateKey(dateKey) {
    return /^\d{4}-\d{2}-\d{2}$/.test(String(dateKey || "")) ?
        dateKey :
        getTodayDateKey();
}

function getSeedData(dateKey) {
    const initialDate = getInitialDateKey(dateKey);
    return {
        startDate: initialDate,
        endDate: initialDate,
        reminderDate: initialDate,
    };
}

function getDecisionFieldsHtml(dateKey) {
    const leaveLocked = isDrugsAndLeaveActionLocked();
    const choices = [
        {
            value: "event",
            icon: "event",
            label: "Event",
        },
        {
            value: "reminder",
            icon: "notifications_active",
            label: "Reminder",
        },
        {
            value: "leave",
            icon: "event_busy",
            label: "Leave Request",
            disabled: leaveLocked,
            title: leaveLocked ? "Leave requests are temporarily locked." : "",
        },
    ];
    return `
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-3" data-calendar-add-entry-modal>
            ${choices.map((choice) => `
                <button
                    type="button"
                    class="calendar-add-entry-choice group flex min-h-28 flex-col items-center justify-center rounded-lg border border-gray-200 bg-white p-4 text-center shadow-sm transition-colors hover:border-[var(--dfc-brand-primary)] hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--dfc-brand-primary-ring)] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-gray-200 sm:min-h-32"
                    data-calendar-add-entry-choice="${attrValue(choice.value)}"
                    data-calendar-add-entry-date="${attrValue(dateKey)}"
                    ${choice.disabled ? "disabled" : ""}
                    ${choice.title ? `title="${attrValue(choice.title)}"` : ""}
                >
                    <span class="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-black transition-colors group-hover:bg-[var(--dfc-brand-primary)] group-hover:text-[var(--dfc-brand-primary-contrast)] group-disabled:group-hover:bg-gray-100 group-disabled:group-hover:text-black">
                        <span class="material-symbols-outlined text-[25px] leading-none" aria-hidden="true">${choice.icon}</span>
                    </span>
                    <span class="text-base font-semibold text-gray-900">${escapeHTML(choice.label)}</span>
                </button>
            `).join("")}
        </div>
    `;
}

function handleChoiceClick(button) {
    if (button.disabled) {return;}
    const seedData = getSeedData(button.dataset.calendarAddEntryDate);
    const choice = button.dataset.calendarAddEntryChoice;
    closeForm();

    if (choice === "event") {
        openEventFormModal(seedData);
    } else if (choice === "leave") {
        openLeaveRequestModal(seedData);
    } else if (choice === "reminder") {
        openReminderFormModal(seedData);
    }
}

/**
 * Opens the Calendar add-entry decision modal.
 * @param {object} [options] Modal options.
 * @param {string} [options.dateKey] Optional YYYY-MM-DD initial date.
 * @returns {void}
 */
export function openCalendarAddEntryModal({ dateKey } = {}) {
    const initialDate = getInitialDateKey(dateKey);
    openStandardForm("calendar_add_entry", null, {
        getFieldsHtml: () => getDecisionFieldsHtml(initialDate),
        afterRender({ formFields, itemForm }) {
            const formTitle = document.querySelector("#form-title");
            const cancelButton = document.getElementById("cancel-form-btn");
            const submitButton = itemForm.querySelector("button[type=\"submit\"]");

            if (formTitle) {
                formTitle.textContent = "Add Calendar Entry";
            }
            if (submitButton) {
                submitButton.style.display = "none";
            }
            if (cancelButton) {
                cancelButton.style.display = "none";
            }
            const formHeaderActions = document.querySelector("#form-header-actions");
            if (formHeaderActions) {
                formHeaderActions.innerHTML = `
                    <button
                        type="button"
                        class="standard-action-icon-btn standard-action-icon-btn--danger shrink-0"
                        aria-label="Close Calendar entry picker"
                        data-calendar-add-entry-close
                    >
                        <span class="material-symbols-outlined text-[20px] leading-none">close</span>
                    </button>
                `;
            }

            itemForm.onsubmit = (event) => event.preventDefault();
            document
                .querySelector("[data-calendar-add-entry-close]")
                ?.addEventListener("click", closeForm);
            formFields
                .querySelectorAll("[data-calendar-add-entry-choice]")
                .forEach((button) => {
                    button.addEventListener("click", () => handleChoiceClick(button));
                });
        },
    });
}
